import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

/**
 * Settings → AI. Choose which LLM processes AI tasks (Grok or Claude), pick the
 * model, set the routing mode, and enter API keys. Config + keys are stored in
 * the admin-only aiConfig/config doc that the ingest functions read.
 *
 * Note: keys live in Firestore (admin-only) so they can be rotated from the UI.
 * For higher-security deployments, move them to Secret Manager later.
 */
type Provider = 'grok' | 'claude';
type RoutingMode = 'all' | 'by_action' | 'each_action';

interface AiConfig {
  textProvider: Provider;
  grokModel: string;
  claudeModel: string;
  routingMode: RoutingMode;
  grokApiKey?: string;
  anthropicApiKey?: string;
}

const DEFAULTS: AiConfig = {
  textProvider: 'grok',
  grokModel: 'grok-4',
  claudeModel: 'claude-sonnet-4-6',
  routingMode: 'all',
};

export function AiSettings() {
  const { role } = useAuth();
  const [cfg, setCfg] = useState<AiConfig>(DEFAULTS);
  const [grokKeyInput, setGrokKeyInput] = useState('');
  const [claudeKeyInput, setClaudeKeyInput] = useState('');
  const [hasGrokKey, setHasGrokKey] = useState(false);
  const [hasClaudeKey, setHasClaudeKey] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'aiConfig', 'config'));
      const d = (snap.exists() ? snap.data() : {}) as Partial<AiConfig>;
      setCfg({ ...DEFAULTS, ...d });
      setHasGrokKey(!!d.grokApiKey);
      setHasClaudeKey(!!d.anthropicApiKey);
      setLoaded(true);
    })().catch(() => setLoaded(true));
  }, []);

  if (role !== 'admin') {
    return <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>;
  }

  async function save() {
    setStatus('saving');
    const payload: Record<string, unknown> = {
      textProvider: cfg.textProvider,
      grokModel: cfg.grokModel.trim() || 'grok-4',
      claudeModel: cfg.claudeModel.trim() || 'claude-sonnet-4-6',
      routingMode: cfg.routingMode,
      updatedAt: serverTimestamp(),
    };
    if (grokKeyInput.trim()) payload.grokApiKey = grokKeyInput.trim();
    if (claudeKeyInput.trim()) payload.anthropicApiKey = claudeKeyInput.trim();
    await setDoc(doc(db, 'aiConfig', 'config'), payload, { merge: true });
    if (grokKeyInput.trim()) {
      setHasGrokKey(true);
      setGrokKeyInput('');
    }
    if (claudeKeyInput.trim()) {
      setHasClaudeKey(true);
      setClaudeKeyInput('');
    }
    setStatus('saved');
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-6 py-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Settings</div>
        <h1 className="text-2xl font-semibold text-stone-800">AI &amp; models</h1>
        <p className="mt-1 text-sm text-stone-500">
          Choose which LLM converts, recognizes, and classifies your documents. Grok (xAI) is the
          default; Claude is available for vision/scanned PDFs and as an alternate text classifier.
        </p>
      </header>

      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-stone-800">Which LLM processes AI tasks</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Text classification provider</span>
            <select
              value={cfg.textProvider}
              onChange={(e) => setCfg({ ...cfg, textProvider: e.target.value as Provider })}
              className="rounded border border-stone-300 px-2 py-1.5 text-sm"
            >
              <option value="grok">Grok (xAI)</option>
              <option value="claude">Claude (Anthropic)</option>
            </select>
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Routing mode</span>
            <select
              value={cfg.routingMode}
              onChange={(e) => setCfg({ ...cfg, routingMode: e.target.value as RoutingMode })}
              className="rounded border border-stone-300 px-2 py-1.5 text-sm"
            >
              <option value="all">Choose LLM for all tasks</option>
              <option value="by_action">Choose LLM by action</option>
              <option value="each_action">Choose LLM each action</option>
            </select>
          </label>
        </div>
        <div className="rounded bg-stone-50 px-3 py-2 text-[11px] text-stone-500">
          {cfg.routingMode === 'all' && 'Every AI task uses the provider above.'}
          {cfg.routingMode === 'by_action' &&
            'Background ingest uses the provider above; interactive tools (Ask SFP) use it too — per-action overrides are coming.'}
          {cfg.routingMode === 'each_action' &&
            'Interactive AI (Ask SFP) will prompt you to pick a provider each time. Background ingest still uses the provider above (it can’t prompt).'}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Grok model</span>
            <input
              value={cfg.grokModel}
              onChange={(e) => setCfg({ ...cfg, grokModel: e.target.value })}
              placeholder="grok-4"
              className="rounded border border-stone-300 px-2 py-1.5 font-mono text-xs"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Claude model</span>
            <input
              value={cfg.claudeModel}
              onChange={(e) => setCfg({ ...cfg, claudeModel: e.target.value })}
              placeholder="claude-sonnet-4-6"
              className="rounded border border-stone-300 px-2 py-1.5 font-mono text-xs"
            />
          </label>
        </div>
      </section>

      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-stone-800">API keys</h2>
        <KeyRow
          label="Grok (xAI) API key"
          configured={hasGrokKey}
          value={grokKeyInput}
          onChange={setGrokKeyInput}
          placeholder="xai-…"
          hint="Get one at console.x.ai. Used for document classification."
        />
        <KeyRow
          label="Claude (Anthropic) API key"
          configured={hasClaudeKey}
          value={claudeKeyInput}
          onChange={setClaudeKeyInput}
          placeholder="sk-ant-…"
          hint="Optional — used for scanned PDFs/images (vision) and as the alternate text classifier."
        />
        <p className="text-[10px] text-stone-400">
          Keys are stored in the admin-only aiConfig document and read by the ingest functions. Only
          admins can see this page.
        </p>
      </section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!loaded || status === 'saving'}
          className="rounded bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save AI settings'}
        </button>
        <Link to="/settings" className="text-xs text-stone-500 hover:text-stone-800">
          ← Back to settings
        </Link>
      </div>
    </div>
  );
}

function KeyRow({
  label,
  configured,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  configured: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  hint: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-stone-700">{label}</span>
        <span
          className={
            configured
              ? 'rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-700'
              : 'rounded bg-stone-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-stone-500'
          }
        >
          {configured ? 'Configured' : 'Not set'}
        </span>
      </div>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={configured ? '•••••••• (enter a new key to replace)' : placeholder}
        className="mt-1 w-full rounded border border-stone-300 px-3 py-2 font-mono text-xs"
        autoComplete="off"
      />
      <div className="mt-1 text-[10px] text-stone-400">{hint}</div>
    </div>
  );
}
